import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/policy/")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 space-y-6 text-foreground">
      <header className="space-y-3">
        <h1 className="text-3xl lg:text-5xl font-bold tracking-tight">
          Echorai Integritetspolicy
        </h1>
        <p className="font-medium text-muted-foreground">
          Gäller från och med: 2025-12-22
        </p>
      </header>

      <p>
        På Echorai tar vi din personliga integritet på största allvar. Denna
        integritetspolicy beskriver hur vi samlar in, använder och skyddar
        personuppgifter när du besöker vår webbplats eller registrerar dig för
        tidig åtkomst innan våra tjänster lanseras.
      </p>

      <section className="space-y-4">
        <h2 className="text-xl font-bold tracking-tight mt-8">
          1. Personuppgifter vi samlar in och varför
        </h2>
        <p>
          Under förlanseringsfasen samlar Echorai endast in begränsade
          personuppgifter som är nödvändiga för att kunna kommunicera med dig
          och förbereda lanseringen.
        </p>

        <h3 className="text-lg font-semibold mt-4">a) Kontaktuppgifter</h3>
        <p>Såsom namn och e-postadress, som samlas in när du:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Anmäler dig för tidig åtkomst eller notifieringar</li>
          <li>Kontaktar oss med frågor eller förfrågningar</li>
        </ul>
        <p className="font-semibold mt-2">Syfte:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Att informera dig om lansering, tidig åtkomst eller
            produktuppdateringar
          </li>
          <li>
            Att kommunicera viktig information, inklusive uppdateringar av
            juridiska dokument
          </li>
        </ul>

        <hr className="my-6 border-border" />

        <h3 className="text-lg font-semibold">
          b) Information om förlansering intresse
        </h3>
        <p>
          Vi kan samla in information om vilka tjänster eller funktioner du
          visar intresse för.
        </p>
        <p className="font-semibold mt-2">Syfte:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Att förstå efterfrågan och prioritera utveckling under förlansering
            fasen
          </li>
          <li>
            Att anpassa vår kommunikation baserat på dina uttryckta intressen
          </li>
        </ul>

        <hr className="my-6 border-border" />

        <h3 className="text-lg font-semibold">
          c) Teknisk information och användningsdata
        </h3>
        <p>
          Såsom IP-adress, enhetstyp, webbläsarinformation och övergripande
          användning av webbplatsen.
        </p>
        <p className="font-semibold mt-2">Syfte:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Att säkerställa webbplatsens säkerhet och stabilitet</li>
          <li>
            Att förstå generella användningsmönster och förbättra
            användarupplevelsen
          </li>
        </ul>

        <hr className="my-6 border-border" />

        <h3 className="text-lg font-semibold">d) Cookies</h3>
        <p>
          Vi använder endast strikt nödvändiga cookies som krävs för att
          webbplatsen ska fungera korrekt.
        </p>
        <p>
          Inga marknadsförings- eller spårningscookies används om detta inte
          uttryckligen anges.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-bold tracking-tight mt-8">
          2. Rättslig grund för behandling
        </h2>
        <p>Vi behandlar personuppgifter baserat på:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <b>Ditt samtycke</b> (GDPR artikel 6.1 a) när du registrerar dig
            för tidig åtkomst eller kommunikation
          </li>
          <li>
            <b>Berättigat intresse</b> (GDPR artikel 6.1 f) för grundläggande
            webbplatssäkerhet och funktionalitet
          </li>
        </ul>
        <p>Du kan när som helst återkalla ditt samtycke.</p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-bold tracking-tight mt-8">
          3. Vad vi inte gör
        </h2>
        <p>Under förlanseringsfasen:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Vi skannar, övervakar eller raderar inte personuppgifter från
            tredjepartswebbplatser
          </li>
          <li>Vi utför ingen aktiv identitetsövervakning</li>
          <li>Vi tillhandahåller inga betalda tjänster eller användarkonton</li>
        </ul>
        <p>
          Eventuella framtida tjänster kommer att omfattas av uppdaterade
          policyer som publiceras innan lansering.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-bold tracking-tight mt-8">
          4. Lagring och lagringstid
        </h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Personuppgifter lagras på säkra servrar inom Europeiska unionen
          </li>
          <li>
            Vi sparar personuppgifter endast så länge det är nödvändigt för
            kommunikation under förlanseringen eller enligt lagkrav
          </li>
          <li>
            Du kan när som helst begära att dina uppgifter raderas, och vi
            kommer att hantera detta skyndsamt
          </li>
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-bold tracking-tight mt-8">
          5. Säkerhetsåtgärder
        </h2>
        <p>
          Vi tillämpar lämpliga tekniska och organisatoriska säkerhetsåtgärder,
          inklusive:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Integritet genom design och dataminimering</li>
          <li>
            Begränsad åtkomst till personuppgifter enligt principen om minsta
            behörighet
          </li>
          <li>Kryptering vid överföring och lagring där det är tillämpligt</li>
          <li>Löpande riskbedömning och säkerhetsövervakning</li>
        </ul>
        <p>
          Om en personuppgiftsincident skulle inträffa följer vi fastställda
          rutiner för att bedöma, begränsa och vid behov informera berörda
          parter i enlighet med gällande lagstiftning.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-bold tracking-tight mt-8">
          6. Dina rättigheter
        </h2>
        <p>Enligt GDPR har du rätt att:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Begära tillgång till dina personuppgifter</li>
          <li>Begära rättelse eller radering av dina uppgifter</li>
          <li>Invända mot eller begära begränsning av viss behandling</li>
          <li>När som helst återkalla ditt samtycke</li>
        </ul>
        <p>
          För att utöva dina rättigheter, kontakta oss på{" "}
          <b>info@echorai.com</b>. Vi kan komma att begära identitetsverifiering
          innan vi hanterar känsliga förfrågningar.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-bold tracking-tight mt-8">
          7. Barns integritet
        </h2>
        <p>
          Echorai riktar sig inte till personer under 18 år och vi samlar inte
          medvetet in personuppgifter från barn.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-bold tracking-tight mt-8">
          8. Ändringar av denna policy
        </h2>
        <p>
          Vi kan komma att uppdatera denna integritetspolicy i takt med att
          Echorai utvecklas. Eventuella ändringar publiceras på denna sida med
          uppdaterat ikraftträdandedatum.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-bold tracking-tight mt-8">
          9. Kontaktuppgifter
        </h2>
        <p>
          <b>Echorai</b>
        </p>
        <p>
          E-post: <b>info@echorai.com</b>
        </p>
      </section>
    </article>
  );
}
